/**
 * @file CoreError - Structured errors with codes, cause chaining, and metadata.
 *
 * - **Code**: machine-readable identifier for categorization (`USER_NOT_FOUND`,
 *   `VALIDATION_FAILED`, …)
 * - **Cause**: underlying error preserved for debugging
 * - **Metadata**: structured context that travels with the error through
 *   `toJSON()` and log sinks (frozen at construction; see services.md §
 *   "Error metadata discipline")
 * - **Serialization**: `toJSON()` for log/API output (with full cause chain),
 *   `toString()` for human-readable console output
 *
 * @example
 * ```typescript
 * // Throw
 * throw new CoreError({
 *   code: 'USER_NOT_FOUND',
 *   message: 'User with ID 123 not found',
 *   metadata: { userId: '123' },
 * })
 *
 * // Normalize unknown errors in a catch block
 * catch (err) {
 *   const error = CoreError.from(err, 'OPERATION_FAILED', { userId })
 *   this.error('onSend', error)
 *   throw error
 * }
 *
 * // Force-override the outer code when wrapping
 * catch (err) {
 *   throw CoreError.wrap(err, { code: 'FETCH_FAILED', message: 'Fetch failed' })
 * }
 *
 * // Branch on any code in the chain
 * if (error.hasCode('CONNECTION_REFUSED')) { … }
 * ```
 *
 * @module
 */

/** Structured context attached to a CoreError; frozen at construction. */
export type ErrorMetadata = Record<string, unknown>

/**
 * Configuration for creating a CoreError instance.
 *
 * @property message - Human-readable description.
 * @property code - Machine-readable identifier for programmatic handling.
 * @property cause - Underlying error preserved for debugging.
 * @property metadata - Structured context for logs and debugging.
 */
export type CoreErrorProps = {
  message: string
  code: string
  cause?: Error
  metadata?: ErrorMetadata
}

/**
 * Structured error with code, cause chaining, frozen metadata, and JSON +
 * string serialization. See the file-level docstring for the surface and
 * usage examples.
 */
export class CoreError extends Error {
  /**
   * Wrap any error value into a CoreError, preserving it as the cause and
   * **forcing the supplied code regardless of the inner error's code**. This
   * is the distinction from `from()`: `from(coreErr, 'X')` returns `coreErr`
   * unchanged (preserving its code), whereas `wrap(coreErr, { code: 'X' })`
   * builds a new outer error tagged `'X'` with `coreErr` as cause. Use when
   * a caller's contract requires the outer code (e.g. `EPHEMERAL_START_FAILED`)
   * to win over whatever the inner chain reports.
   */
  static wrap(
    error: unknown,
    options: Omit<CoreErrorProps, 'cause'>,
  ): CoreError {
    const cause = error instanceof Error ? error : undefined
    const inferredMessage =
      error instanceof Error
        ? error.message
        : typeof error === 'string'
          ? error
          : 'Unknown error'

    return new CoreError({
      ...options,
      message: options.message || inferredMessage,
      ...(cause ? { cause } : {}),
      metadata: {
        ...options.metadata,
        ...(!(error instanceof Error) && error !== undefined
          ? { originalError: error }
          : {}),
      },
    })
  }

  /**
   * Normalize any value to a CoreError. If `error` is already a CoreError and
   * no `metadata` is supplied, returns it unchanged; otherwise constructs a
   * new instance preserving the original code (when one exists) and merging
   * `metadata`. Use this for catch-block normalization; use `wrap()` instead
   * when the outer code must override the inner.
   *
   * @param error - Any value to normalize.
   * @param defaultCode - Code to use when `error` isn't already a CoreError
   *   (default: `'UNKNOWN_ERROR'`).
   * @param metadata - Extra context to merge into the resulting error.
   */
  static from(
    error: unknown,
    defaultCode = 'UNKNOWN_ERROR',
    metadata?: ErrorMetadata,
  ): CoreError {
    if (error instanceof CoreError) {
      if (!metadata) return error
      return new CoreError({
        code: error.code,
        message: error.message,
        cause: error.cause,
        metadata: { ...error.metadata, ...metadata },
      })
    }

    if (error instanceof Error) {
      return new CoreError({
        code: defaultCode,
        message: error.message,
        cause: error,
        ...(metadata && { metadata }),
      })
    }

    return new CoreError({
      code: defaultCode,
      message: typeof error === 'string' ? error : 'An unknown error occurred',
      metadata: { ...metadata, originalError: error },
    })
  }

  public override readonly name: string = 'CoreError'
  public readonly code: string
  public override readonly cause?: Error
  public readonly metadata: ErrorMetadata
  public readonly timestamp: Date

  constructor(props: CoreErrorProps) {
    super(props.message)

    this.code = props.code
    if (props.cause) {
      this.cause = props.cause
    }
    // Freeze metadata so post-construction mutation (`error.metadata.x = …`)
    // throws instead of silently changing what the log pipeline already
    // serialised. Per services.md § "Error metadata discipline": metadata is
    // the single channel for structured context, and the freeze enforces its
    // append-only-through-`CoreError.from(...)` contract at the language
    // level.
    this.metadata = Object.freeze(props.metadata ?? {})
    this.timestamp = new Date()

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor)
    }
  }

  /**
   * All errors in the cause chain, from this error to the root. Handles
   * circular references safely.
   */
  getErrorChain(): Error[] {
    const chain: Error[] = [this]
    const seen = new Set<Error>([this])
    let current: Error | undefined = this.cause

    while (current && !seen.has(current)) {
      seen.add(current)
      chain.push(current)
      current = current instanceof CoreError ? current.cause : undefined
    }

    return chain
  }

  /**
   * Serialize to a JSON-compatible object including the full cause chain.
   *
   * @param includeStack - Whether to include stack traces (default: true).
   *   Set to `false` for client-facing API responses.
   */
  toJSON(includeStack = true) {
    const json: any = {
      name: this.name,
      code: this.code,
      message: this.message,
      timestamp: this.timestamp.toISOString(),
      metadata: this.metadata,
    }

    if (includeStack && this.stack) {
      json.stack = this.stack
    }

    const causes = this.getErrorChain().slice(1)
    if (causes.length > 0) {
      const causeChain = causes.map(err => {
        const entry: any = { name: err.name, message: err.message }
        if (err instanceof CoreError) {
          entry.code = err.code
          entry.metadata = err.metadata
        }
        if (includeStack && err.stack) {
          entry.stack = err.stack
        }
        return entry
      })
      json.causeChain = causeChain
      // A fresh scalar summary, NOT a reference back into `causeChain`.
      // Reusing the `causeChain[last]` object here makes it appear twice in
      // the emitted graph; a circular-safe log serializer marks the second
      // occurrence "[Circular]" even though the graph is acyclic. Full
      // detail (metadata, stack) stays in `causeChain`.
      const root = causeChain[causeChain.length - 1]
      json.rootCause = {
        name: root.name,
        message: root.message,
        ...(root.code !== undefined ? { code: root.code } : {}),
      }
    }

    return json
  }

  /**
   * Format as a human-readable multi-line string with cause chain
   * visualization. Useful for console output and debugging:
   *
   * ```
   * [USER_NOT_FOUND] User with ID 123 not found
   *   Metadata: {"userId":"123"}
   *   └─ Caused by: [CONNECTION_REFUSED] Connection refused
   *     └─ Caused by: Error: ECONNREFUSED
   *       └─ Root Cause
   * ```
   */
  override toString(): string {
    const lines: string[] = []
    lines.push(`[${this.code}] ${this.message}`)
    if (Object.keys(this.metadata).length > 0) {
      lines.push(`  Metadata: ${JSON.stringify(this.metadata)}`)
    }

    let current: Error | undefined = this.cause
    let depth = 1
    const indent = '  '

    while (current) {
      const prefix = indent.repeat(depth) + '└─ Caused by: '
      if (current instanceof CoreError) {
        lines.push(`${prefix}[${current.code}] ${current.message}`)
        if (Object.keys(current.metadata).length > 0) {
          const meta = JSON.stringify(current.metadata)
          lines.push(`${indent.repeat(depth + 1)}Metadata: ${meta}`)
        }
      } else {
        lines.push(`${prefix}${current.name}: ${current.message}`)
      }

      const nextCause = current instanceof CoreError ? current.cause : undefined
      if (!nextCause) {
        lines.push(`${indent.repeat(depth + 1)}└─ Root Cause`)
      }

      current = nextCause
      depth++
    }

    if (this.stack) {
      lines.push('')
      lines.push('Stack Trace:')
      lines.push(this.stack)
    }

    return lines.join('\n')
  }

  /** True if any CoreError in the chain has the specified code. */
  hasCode(code: string): boolean {
    return this.getErrorChain().some(
      err => err instanceof CoreError && err.code === code,
    )
  }
}
